import type { ICarePlan } from "@domain/CarePlan";
import
{
    type IVehicleConditionDto,
    type IVehicleCondition,
    type IVehicle,
    type ICondition,
    ConditionType,
    ServiceCondition,
    Conditions,
    type IConditionDto,
    ConditionState,
} from "..";
import { ServiceSchedule, type IServiceSchedule } from "../ServiceSchedule";

export class VehicleCondition implements IVehicleCondition
{

    conditions: Conditions;
    vehicle: IVehicle;
    serviceSchedule: IServiceSchedule;
    score: number = 0;

    constructor( vehicle: IVehicle, model: IVehicleConditionDto )
    {

        this.vehicle = vehicle;

        // Create the conditions
        this.conditions = new Conditions( this.vehicle, model.conditions );

        // Generate the service schedule
        this.serviceSchedule = new ServiceSchedule( model.serviceSchedule );
        this.conditions.get( ServiceCondition ).setServiceSchedule( this.serviceSchedule );
        
        this.initialise();

    }

    /**
     * Initialises the vehicle condition.  Must be left re-entrant as it can be called 
     * after updates to reinitialise the state
     * @param updatedConditions Optional array of one or more condition dto's that have been updated on the backend and need to be reinitialised 
     */
    initialise( updatedConditions?: IConditionDto[] )
    {
        // Re-initialise any updated conditions
        updatedConditions?.forEach( c => this.updateCondition( c ) );

        // Recalculate
        this.calculateScore();
        
    }

    /**
     * Updates the conditions with the care plan states, depending on what care plan products the consumer has
     * @param carePlan 
     */
    applyCarePlan( carePlan: ICarePlan )
    {
        for( const condition of this.conditions )
        {
            if( condition.applyCarePlan == null ) continue;
            condition.applyCarePlan( carePlan );
        }
    }

    /**
     * Updates the condition with new data from the dto
     * @param condition 
     */
    private updateCondition( dto: IConditionDto )
    {
        var condition = this.conditions.find( c => c.conditionId == dto.conditionId );
        condition?.reinitialise( dto );
    }
    
    /**
     * Gets the condition that matches the type
     * @param type 
     * @returns 
     */
    getCondition( type: ConditionType ): ICondition | undefined
    {
        return this.conditions.find( c => c.type == type );
    }
    
    /**
     * Gets all conditions that need attention, ordered by priority (state)
     */
    get conditionsWithAlerts()
    {
        const result = this.conditions.filter( c => c.hasAlert );
        result.sort( ( a, b ) => b.state - a.state ); // sort by state descending
        return result;
    }
    
    /**
     * Gets all conditions that are due
     */
    get conditionsDue()
    {
        const result = this.conditions.filter( c => c.state == ConditionState.Due );
        result.sort( ( a, b ) => b.state - a.state ); // sort by state descending
        return result;
    }

    /**
     * Gets all conditions that are overdue or critical
     */
    get conditionsOverdueOrCritical()
    {
        const result = this.conditions.filter( c => c.state == ConditionState.Critical || c.state == ConditionState.Overdue );
        result.sort( ( a, b ) => b.state - a.state ); // sort by state descending
        return result;
    }

    /**
     * Gets all conditions that are critical
     */
    get conditionsCritical()
    {
        const result = this.conditions.filter( c => c.state == ConditionState.Critical );
        result.sort( ( a, b ) => b.state - a.state ); // sort by state descending
        return result;
    }

    /**
     * Calculates the condition score
     */
    calculateScore()
    {
        
        // Start out with three points for each condition, of the set
        // of conditions that are scored (we dont include recalls or disabled conditions)
        const eligibleConditions = this.conditions.filter( c => c.type != ConditionType.Recall && c.isDisabled == false );
        const potentialScore = eligibleConditions.length * 3; 
        
        let deductions = 0;
        deductions += eligibleConditions.filter( c => c.isUnconfigured ).length; // lose one point for each condtion that is in an unknown state
        deductions += eligibleConditions.filter( c => c.isCritical ).length * 2; // lose two points for each condtion that is critical
        deductions += eligibleConditions.filter( c => c.isOverdue || ( c.isDue && c.score <= 0.1 ) ).length; // lose one point for each condition that is overdue

        const score = potentialScore - deductions;
        this.score = Math.round( score / potentialScore * 100 );

    }

}